import { cn } from '@ordo/ui/cn'
import { Skeleton } from '@ordo/ui/skeleton'

import {
  SERVE_ROW_GRID_CLASS,
  SERVE_ROW_MEAL_COLUMN_CLASS,
  SERVE_ROW_NAME_COLUMN_CLASS,
} from '@/components/cafeteria/serve/inline-serve-row'
import { SERVE_CONTENT_WIDTH_CLASS } from '@/components/cafeteria/serve/serve-layout'

export interface CafeteriaServeSkeletonProps {
  /**
   * Stand in the single-column line's own box: centered, capped at 1100px
   * (`SERVE_CONTENT_WIDTH_CLASS`) with the 16px side / bottom inset (the
   * page and the route fallback). Tablet mode's rows column already insets
   * its content and runs full width, so the page passes `false` there to
   * keep the rows flush with the search above them.
   */
  padded?: boolean
  /** Tablet mode: the row's narrower (200 / 170) columns, and their own stacking threshold. */
  compact?: boolean
}

/**
 * Cold-load skeleton for the serve screen's line, block-for-block with the
 * inline row (PROD-1428 rounds 3 + 7): the same card (16px radius, 1px
 * border, a `@container`), the same grid (16px top/bottom, 20px sides, 16px
 * column gap, top-aligned columns, 240 / fill / 214 on a desktop, stacked
 * under the row's own container-query thresholds), the name column behind its
 * divider (a 24/32 name over a 16/24 meta line 4px under it, a chip 12px
 * under that), two 18/28 item lines 8px apart (nudged onto the name's
 * baseline like the row's), and the 72px pill in the
 * action column. The header and the search field render for real above it
 * (their facts come from the sessions list, and the search must exist from
 * the first paint so the keyboard can land on it), so neither is mirrored
 * here. Six rows, 10px apart, like the list.
 */
export function CafeteriaServeSkeleton({ padded = true, compact = false }: CafeteriaServeSkeletonProps) {
  const density = compact ? 'compact' : 'desktop'
  return (
    <div className={cn('flex flex-col gap-2.5', padded && cn(SERVE_CONTENT_WIDTH_CLASS, 'px-4 pb-4'))} aria-busy>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="@container rounded-2xl border bg-background" data-density={density}>
          <div className={cn('grid items-start gap-x-4 gap-y-3 px-5 py-4', SERVE_ROW_GRID_CLASS[density])}>
            {/* The same name column as the row: stacked across a narrow card, a divider on a wide one. */}
            <div className={SERVE_ROW_NAME_COLUMN_CLASS[density]}>
              <Skeleton className="h-8 w-40" />
              <Skeleton className={cn('h-6 w-28', compact ? '@[722px]:mt-1' : '@[806px]:mt-1')} />
              <Skeleton className={cn('h-5 w-20 rounded-full', compact ? '@[722px]:mt-3' : '@[806px]:mt-3')} />
            </div>
            <div className={cn('flex flex-col gap-2', SERVE_ROW_MEAL_COLUMN_CLASS[density])}>
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-7 w-1/2" />
            </div>
            <Skeleton className="h-[72px] w-full rounded-full" />
          </div>
        </div>
      ))}
    </div>
  )
}
